import { Component, ElementRef, EventEmitter, Input, OnChanges, Output,
         SimpleChanges, ViewChild
       } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { RegionLike } from 'app/controls/image-cropper/region';
import { Utils } from 'core/utils';


@Component({
    selector: 'image-zoom',
    templateUrl: './image-zoom.component.html',
    styleUrls: ['./image-zoom.component.scss'],
    standalone: false
})

export class ImageZoomComponent implements OnChanges
{
  @Input() public selectedRegion: RegionLike|undefined = undefined;
  @Input() public targetRegion: RegionLike|undefined = undefined;

  @Output() public selectedRegionChange: EventEmitter<RegionLike> =
      new EventEmitter<RegionLike>();

  @ViewChild('zoom', {static: true})
  private zoom: ElementRef;
  private maxRegion: RegionLike|undefined = undefined;

  public readonly MAX_ZOOM: number = 0.95;

  constructor(
      private mir: MatIconRegistry,
      private sanitizer: DomSanitizer)
  {
    this.mir.addSvgIcon(
        'glass-minus',
        this.sanitizer.bypassSecurityTrustResourceUrl(Utils.url(
            'assets/img/icons/glass-minus.svg')));
    this.mir.addSvgIcon(
        'glass-plus',
        this.sanitizer.bypassSecurityTrustResourceUrl(Utils.url(
            'assets/img/icons/glass-plus.svg')));
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['selectedRegion']) {
      this.updateZoomInput();
    }
    if (changes['targetRegion']) {
      this.updateMaxRegion();
    }
  }

  private updateMaxRegion(): void
  {
    if (!this.selectedRegion) {
      return;
    }

    const cropW = this.selectedRegion.width;
    const cropH = this.selectedRegion.height;

    if (this.targetRegion.width / this.targetRegion.height < cropW / cropH) {
      this.maxRegion = {
          x: 0,
          y: 0,
          width: this.targetRegion.width,
          height:
              this.targetRegion.width / cropW * cropH,
      };
    }
    else {
      this.maxRegion = {
          x: 0,
          y: 0,
          width:
              this.targetRegion.height / cropH * cropW,
          height: this.targetRegion.height,
      };
    }
  }

  private updateZoomInput(): void
  {
    if (!this.maxRegion) {
      this.updateMaxRegion();
    }

    if (this.selectedRegion.width < this.selectedRegion.height) {
      this.zoom.nativeElement.value =
        1 - this.selectedRegion.width / this.maxRegion.width;
    }
    else {
      this.zoom.nativeElement.value =
        1 - this.selectedRegion.height / this.maxRegion.height;
    }
  }

  private makeZoom(zoom: number): void
  {
    if (!this.maxRegion) {
      this.updateMaxRegion();
    }
    if (zoom < 0) {
      zoom = 0;
    }
    if (zoom > this.MAX_ZOOM) {
      zoom = this.MAX_ZOOM;
    }

    const scale = 1 - zoom;
    const centerX = (this.selectedRegion.x + this.selectedRegion.width / 2);
    const centerY = (this.selectedRegion.y + this.selectedRegion.height / 2);
    const newWidth  = this.maxRegion.width * scale;
    const newHeight = this.maxRegion.height * scale;

    let newX = centerX - newWidth / 2;
    let newY = centerY - newHeight / 2;
    if (newX < 0) {
      newX = 0;
    }
    if (newY < 0) {
      newY = 0;
    }
    if (newX + newWidth > this.targetRegion.width) {
      newX = this.targetRegion.width - newWidth;
    }
    if (newY + newHeight > this.targetRegion.height) {
      newY = this.targetRegion.height - newHeight;
    }
    this.selectedRegion = {
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight
    };
    this.selectedRegionChange.emit(this.selectedRegion);
    this.updateZoomInput();
  }

  public zoomInputChanged(event: any): void
  {
    this.makeZoom(parseFloat(event.target.value));
  }

  public decreaseZoom(): void
  {
    this.makeZoom(parseFloat(this.zoom.nativeElement.value) - 0.05);
  }

  public increaseZoom(): void
  {
    this.makeZoom(parseFloat(this.zoom.nativeElement.value) + 0.05);
  }
}
